import { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import type { DropShippingEmailSettings } from '../../../lib/api/services/auth';

const emptySettings: DropShippingEmailSettings = {
  recipientEmailsEnabledByDefault: false,
  brandName: '',
  logoUrl: '',
  headerImageUrl: '',
  primaryColor: '#0d0d0d',
  accentColor: '#f6cf02',
  replyToEmail: '',
  footerText: ''
};

const DropShippingEmailSettingsPage = () => {
  const { auth } = useAuth();
  const [settings, setSettings] = useState<DropShippingEmailSettings>(emptySettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<'logo' | 'header' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (auth.user?.role !== 'distributor') {
        setLoading(false);
        return;
      }

      try {
        const response = await api.auth.getDropShippingEmailSettings();
        if (response.success && response.data) {
          setSettings(response.data);
        }
      } catch (err: any) {
        setError(err?.message || 'No se pudo cargar la configuración');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [auth.user?.role]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target;
    const checked = type === 'checkbox' ? (event.target as HTMLInputElement).checked : undefined;

    setSettings((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      const response = await api.auth.updateDropShippingEmailSettings(settings);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'No se pudieron guardar los cambios');
      }

      setSettings(response.data);
      setMessage('Configuración guardada');
    } catch (err: any) {
      setError(err?.message || 'No se pudieron guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (assetType: 'logo' | 'header', file?: File | null) => {
    if (!file) {
      return;
    }

    setUploading(assetType);
    setMessage(null);
    setError(null);

    try {
      const response = await api.auth.uploadDropShippingEmailAsset(assetType, file);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'No se pudo subir la imagen');
      }

      setSettings(response.data.settings);
      setMessage(assetType === 'logo' ? 'Logo subido a R2' : 'Imagen de cabecera subida a R2');
    } catch (err: any) {
      setError(err?.message || 'No se pudo subir la imagen');
    } finally {
      setUploading(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="animate-pulse h-8 w-64 bg-gray-200 rounded mb-6" />
        <div className="animate-pulse h-72 bg-gray-100 rounded-2xl" />
      </div>
    );
  }

  if (auth.user?.role !== 'distributor') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Emails para cliente final</h1>
          <p className="text-gray-600">Esta configuración sólo está disponible para distribuidores.</p>
          <Link to="/account" className="inline-block mt-6 text-sm font-medium text-black underline">
            Volver a mi cuenta
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-12">
      <div className="max-w-6xl mx-auto px-4 space-y-8">
        <div className="flex items-start justify-between gap-6">
          <div>
            <Link to="/account" className="text-sm font-medium text-black underline">
              Volver a mi cuenta
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mt-4">Emails para cliente final</h1>
            <p className="text-gray-600 mt-3 max-w-2xl">
              Configura el branding de los emails logísticos que, de forma opcional, recibirán tus clientes finales.
              El logo y la cabecera se suben a R2 y quedan disponibles para reutilizarse en tus pedidos.
            </p>
          </div>
        </div>

        {message && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
            {message}
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_0.9fr] gap-8">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Nombre de marca</span>
                <input
                  name="brandName"
                  value={settings.brandName}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Reply-to</span>
                <input
                  name="replyToEmail"
                  type="email"
                  value={settings.replyToEmail}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Color principal</span>
                <input
                  name="primaryColor"
                  value={settings.primaryColor}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Color acento</span>
                <input
                  name="accentColor"
                  value={settings.accentColor}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3"
                />
              </label>
            </div>

            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-2">Pie legal o mensaje final</span>
              <textarea
                name="footerText"
                value={settings.footerText}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-xl border border-gray-300 px-4 py-3"
              />
            </label>

            <label className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
              <input
                type="checkbox"
                name="recipientEmailsEnabledByDefault"
                checked={settings.recipientEmailsEnabledByDefault}
                onChange={handleChange}
                className="h-4 w-4"
              />
              <span className="text-sm text-amber-900">
                Activar por defecto el envío opcional de email logístico al destinatario final.
              </span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="rounded-2xl border border-dashed border-gray-300 p-5">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Logo</h2>
                    <p className="text-sm text-gray-500">Subida directa a R2.</p>
                  </div>
                  {settings.logoUrl && (
                    <a href={settings.logoUrl} target="_blank" rel="noreferrer" className="text-sm underline">
                      Ver archivo
                    </a>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleUpload('logo', event.target.files?.[0])}
                  className="block w-full text-sm"
                />
                {uploading === 'logo' && <p className="text-sm text-gray-500 mt-2">Subiendo logo...</p>}
              </div>

              <div className="rounded-2xl border border-dashed border-gray-300 p-5">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Imagen cabecera</h2>
                    <p className="text-sm text-gray-500">Opcional, también subida a R2.</p>
                  </div>
                  {settings.headerImageUrl && (
                    <a href={settings.headerImageUrl} target="_blank" rel="noreferrer" className="text-sm underline">
                      Ver archivo
                    </a>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleUpload('header', event.target.files?.[0])}
                  className="block w-full text-sm"
                />
                {uploading === 'header' && <p className="text-sm text-gray-500 mt-2">Subiendo cabecera...</p>}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-black text-white px-6 py-3 text-sm font-medium"
              >
                {saving ? 'Guardando...' : 'Guardar configuración'}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
            <div
              className="px-8 py-10 text-center"
              style={{ backgroundColor: settings.primaryColor }}
            >
              {settings.headerImageUrl && (
                <img
                  src={settings.headerImageUrl}
                  alt="Cabecera"
                  className="w-full rounded-2xl mb-6 object-cover max-h-40"
                />
              )}
              {settings.logoUrl && (
                <img
                  src={settings.logoUrl}
                  alt={settings.brandName}
                  className="h-16 w-auto mx-auto mb-4 object-contain"
                />
              )}
              <p className="text-xs uppercase tracking-[0.22em] text-white/80">Preview</p>
              <h2 className="text-3xl font-semibold text-white mt-3">{settings.brandName || 'Tu marca'}</h2>
            </div>
            <div className="p-8">
              <div
                className="inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]"
                style={{ backgroundColor: settings.accentColor, color: '#111827' }}
              >
                Pedido enviado
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mt-5">Hola Marta,</h3>
              <p className="text-gray-600 mt-3 leading-7">
                Tu distribuidor ha solicitado el envío y ya estamos preparando tu pedido.
              </p>
              <div className="rounded-2xl bg-stone-50 border border-stone-200 p-5 mt-6">
                <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Dirección de entrega</p>
                <p className="text-gray-900 mt-2 leading-7">
                  Calle Ejemplo 25<br />
                  41001 Sevilla<br />
                  España
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-6">
                Responder a: <span className="font-medium text-gray-800">{settings.replyToEmail || 'tu-email@marca.com'}</span>
              </p>
            </div>
            <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600 whitespace-pre-line">
                {settings.footerText || 'Aquí aparecerá tu texto de soporte o pie legal.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DropShippingEmailSettingsPage;
